/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/TenantBotConfig.tsx
 * PURPOSE: Tenant Admin Bot Configuration — 4 tabs (general, hours, auto-response, channels)
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + 8-Mental-Model + Agentic)
 * DATE: 2026-03-21
 * COMPLIANCE: SDLC 6.3.1 + DD-0018 JSONB Storage
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Tenant Admin Module 6 — Bot & Automation Configuration
 * - Impact: Operational — configures chatbot behavior for tenant
 * - Stakeholders: Tenant Admins, End Users (chat recipients)
 *
 * @technical-architecture
 * - Layer: Presentation Layer (React 18)
 * - Pattern: Tab navigation, form validation, CRUD for auto-responses
 * - Dependencies: apiClient, Sprint 79L bot_config.py backend
 *
 * @cross-references
 * - Backend: backend/app/api/v2/tenant_admin/bot_config.py
 * - Sprint Plan: SPRINT-079L-TA-SECURITY-AUDIT-BOT-CONFIG-LOCAL.md
 */

import React, { useState, useCallback, useEffect } from "react";
import { apiClientV2 as apiClient } from "@api/v2";

type BotTab = "general" | "hours" | "auto-response" | "channels";

interface BotConfig {
  bot_enabled: boolean;
  bot_name: string;
  welcome_message: string;
  auto_reply_enabled: boolean;
  working_hours_only: boolean;
  working_hours_start: string;
  working_hours_end: string;
  supported_channels: string[];
  language: string;
  fallback_message: string;
}

interface AutoResponseRule {
  id: string;
  keywords: string[];
  response: string;
  priority: number;
  is_active: boolean;
}

const TABS: Array<{ id: BotTab; label: string; icon: string }> = [
  { id: "general", label: "Cài đặt chung", icon: "⚙️" },
  { id: "hours", label: "Giờ hoạt động", icon: "🕐" },
  { id: "auto-response", label: "Trả lời tự động", icon: "💬" },
  { id: "channels", label: "Kênh kết nối", icon: "🔗" },
];

const CHANNELS = [
  { id: "web", label: "Web Chat", icon: "🌐", desc: "Chat trên website" },
  { id: "zalo", label: "Zalo OA", icon: "💙", desc: "Zalo Official Account" },
  { id: "telegram", label: "Telegram", icon: "✈️", desc: "Telegram Bot" },
  { id: "facebook", label: "Facebook", icon: "📘", desc: "Facebook Messenger" },
];

const TenantBotConfig: React.FC = () => {
  const [activeTab, setActiveTab] = useState<BotTab>("general");
  const [config, setConfig] = useState<BotConfig | null>(null);
  const [rules, setRules] = useState<AutoResponseRule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [newKeyword, setNewKeyword] = useState("");
  const [newResponse, setNewResponse] = useState("");

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const [cfgRes, rulesRes] = await Promise.allSettled([
        apiClient.get("/api/v2/tenant-admin/bot/config"),
        apiClient.get("/api/v2/tenant-admin/bot/auto-responses"),
      ]);
      if (cfgRes.status === "fulfilled") setConfig(cfgRes.value.data);
      if (rulesRes.status === "fulfilled") setRules(rulesRes.value.data.rules || []);
    } catch (err: any) {
      console.error("Lỗi tải cấu hình bot:", err?.message);
      setSaveMessage("Lỗi tải cấu hình bot. Vui lòng thử lại.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveConfig = async (updates: Partial<BotConfig>) => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const res = await apiClient.patch("/api/v2/tenant-admin/bot/config", updates);
      setConfig(res.data);
      setSaveMessage("Đã lưu thành công");
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (err) {
      setSaveMessage("Lỗi khi lưu cấu hình");
    } finally {
      setIsSaving(false);
    }
  };

  const addRule = async () => {
    if (!newKeyword.trim() || !newResponse.trim()) return;
    try {
      await apiClient.post("/api/v2/tenant-admin/bot/auto-responses", {
        keywords: newKeyword.split(",").map((k) => k.trim()).filter(Boolean),
        response: newResponse,
        priority: rules.length,
      });
      setNewKeyword("");
      setNewResponse("");
      fetchConfig();
    } catch (err: any) {
      alert(err?.response?.data?.detail || "Lỗi khi tạo quy tắc");
    }
  };

  const deleteRule = async (ruleId: string) => {
    if (!confirm("Xóa quy tắc này?")) return;
    try {
      await apiClient.delete(`/api/v2/tenant-admin/bot/auto-responses/${ruleId}`);
      fetchConfig();
    } catch (err) {
      alert("Lỗi khi xóa quy tắc");
    }
  };

  if (isLoading) {
    return <div style={{ padding: "48px", textAlign: "center", color: "#6b7280" }}>Đang tải cấu hình bot...</div>;
  }
  if (!config) {
    return (
      <div style={{ padding: "48px", textAlign: "center", color: "#dc2626" }}>
        <p style={{ marginBottom: "16px" }}>{saveMessage || "Không tải được cấu hình bot."}</p>
        <button
          onClick={fetchConfig}
          style={{ padding: "8px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
        >
          Thử lại
        </button>
      </div>
    );
  }

  return (
    // 2026-08-25 (UI-UX-DECISION-REGISTER.md luật 9, rollout): bỏ maxWidth
    // 1000px + margin:auto — kéo sát lề khả dụng ở Desktop.
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "bold", color: "#1f2937", margin: 0 }}>Cấu hình Bot</h1>
        {saveMessage && (
          <span style={{ fontSize: "14px", color: saveMessage.includes("Lỗi") ? "#dc2626" : "#22c55e" }}>
            {isSaving ? "Đang lưu..." : saveMessage}
          </span>
        )}
      </div>

      {/* Bot Enable Toggle */}
      <div style={{ background: "white", borderRadius: "8px", padding: "16px 20px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <div>
          <div style={{ fontSize: "16px", fontWeight: "600", color: "#1f2937" }}>Bot {config.bot_enabled ? "đang hoạt động" : "đã tắt"}</div>
          <div style={{ fontSize: "13px", color: "#6b7280" }}>Bật/tắt bot cho toàn bộ tenant</div>
        </div>
        <button
          onClick={() => saveConfig({ bot_enabled: !config.bot_enabled })}
          style={{
            padding: "8px 20px",
            background: config.bot_enabled ? "#dc2626" : "#22c55e",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          {config.bot_enabled ? "Tắt Bot" : "Bật Bot"}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "20px", background: "white", borderRadius: "8px", padding: "4px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1, padding: "10px", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px",
              fontWeight: activeTab === tab.id ? "600" : "400",
              background: activeTab === tab.id ? "#2563eb" : "transparent",
              color: activeTab === tab.id ? "white" : "#6b7280",
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ background: "white", borderRadius: "12px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        {activeTab === "general" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div>
              <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151", display: "block", marginBottom: "6px" }}>Tên Bot</label>
              <input type="text" value={config.bot_name} onChange={(e) => setConfig({ ...config, bot_name: e.target.value })}
                onBlur={() => saveConfig({ bot_name: config.bot_name })}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }} />
            </div>
            <div>
              <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151", display: "block", marginBottom: "6px" }}>Tin nhắn chào mừng</label>
              <textarea value={config.welcome_message} onChange={(e) => setConfig({ ...config, welcome_message: e.target.value })}
                onBlur={() => saveConfig({ welcome_message: config.welcome_message })} rows={3}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151", display: "block", marginBottom: "6px" }}>Tin nhắn khi không hiểu</label>
              <textarea value={config.fallback_message} onChange={(e) => setConfig({ ...config, fallback_message: e.target.value })}
                onBlur={() => saveConfig({ fallback_message: config.fallback_message })} rows={2}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151", display: "block", marginBottom: "6px" }}>Ngôn ngữ</label>
              <select value={config.language} onChange={(e) => { setConfig({ ...config, language: e.target.value }); saveConfig({ language: e.target.value }); }}
                style={{ padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}>
                <option value="vi">Tiếng Việt</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === "hours" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>Chỉ hoạt động trong giờ làm việc</div>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>Bot sẽ không trả lời ngoài giờ</div>
              </div>
              <button onClick={() => saveConfig({ working_hours_only: !config.working_hours_only })}
                style={{ padding: "6px 16px", background: config.working_hours_only ? "#2563eb" : "#9ca3af", color: "white", border: "none", borderRadius: "20px", cursor: "pointer", fontSize: "13px" }}>
                {config.working_hours_only ? "Bật" : "Tắt"}
              </button>
            </div>
            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151", display: "block", marginBottom: "6px" }}>Giờ bắt đầu</label>
                <input type="time" value={config.working_hours_start} onChange={(e) => { setConfig({ ...config, working_hours_start: e.target.value }); saveConfig({ working_hours_start: e.target.value }); }}
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151", display: "block", marginBottom: "6px" }}>Giờ kết thúc</label>
                <input type="time" value={config.working_hours_end} onChange={(e) => { setConfig({ ...config, working_hours_end: e.target.value }); saveConfig({ working_hours_end: e.target.value }); }}
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }} />
              </div>
            </div>
          </div>
        )}

        {activeTab === "auto-response" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>{rules.length}/50 quy tắc</div>
              <button onClick={() => saveConfig({ auto_reply_enabled: !config.auto_reply_enabled })}
                style={{ padding: "6px 16px", background: config.auto_reply_enabled ? "#2563eb" : "#9ca3af", color: "white", border: "none", borderRadius: "20px", cursor: "pointer", fontSize: "13px" }}>
                Trả lời tự động: {config.auto_reply_enabled ? "Bật" : "Tắt"}
              </button>
            </div>

            {/* Add new rule */}
            <div style={{ background: "#f9fafb", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
              <div style={{ fontSize: "14px", fontWeight: "500", color: "#374151", marginBottom: "8px" }}>Thêm quy tắc mới</div>
              <input type="text" value={newKeyword} onChange={(e) => setNewKeyword(e.target.value)}
                placeholder="Từ khóa (phân cách bằng dấu phẩy: nghỉ phép, xin nghỉ)"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", marginBottom: "8px" }} />
              <textarea value={newResponse} onChange={(e) => setNewResponse(e.target.value)}
                placeholder="Phản hồi tự động khi phát hiện từ khóa"
                rows={2} style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", resize: "vertical", marginBottom: "8px" }} />
              <button onClick={addRule} disabled={!newKeyword.trim() || !newResponse.trim()}
                style={{ padding: "8px 16px", background: newKeyword.trim() && newResponse.trim() ? "#2563eb" : "#9ca3af", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px" }}>
                Thêm quy tắc
              </button>
            </div>

            {/* Existing rules */}
            {rules.map((rule) => (
              <div key={rule.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 0", borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "4px" }}>
                    {rule.keywords.map((kw, i) => (
                      <span key={i} style={{ padding: "2px 8px", background: "#eff6ff", color: "#2563eb", borderRadius: "12px", fontSize: "12px" }}>{kw}</span>
                    ))}
                  </div>
                  <div style={{ fontSize: "13px", color: "#374151" }}>{rule.response}</div>
                </div>
                <button onClick={() => deleteRule(rule.id)}
                  style={{ padding: "4px 8px", background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "12px" }}>
                  Xóa
                </button>
              </div>
            ))}
            {rules.length === 0 && (
              <div style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>Chưa có quy tắc trả lời tự động</div>
            )}
          </div>
        )}

        {activeTab === "channels" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {CHANNELS.map((ch) => {
              const enabled = config.supported_channels.includes(ch.id);
              return (
                <div key={ch.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "#f9fafb", borderRadius: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ fontSize: "24px" }}>{ch.icon}</span>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937" }}>{ch.label}</div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>{ch.desc}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const newChannels = enabled
                        ? config.supported_channels.filter((c) => c !== ch.id)
                        : [...config.supported_channels, ch.id];
                      saveConfig({ supported_channels: newChannels });
                    }}
                    style={{
                      padding: "6px 16px",
                      background: enabled ? "#22c55e" : "#e5e7eb",
                      color: enabled ? "white" : "#6b7280",
                      border: "none", borderRadius: "20px", cursor: "pointer", fontSize: "13px",
                    }}
                  >
                    {enabled ? "Đã kết nối" : "Kết nối"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantBotConfig;
